import React, { Suspense, lazy } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LoadingRows } from "./components/ui/LoadingExperience";
import ClairLabsLandingPage from "./components/pages/ClairLabsLandingPage";
import { isClairAdsHost } from './config/publicLinks';
import {
  ClientContextGate,
  ClientSelectionPage,
  LimitedAccessPage,
} from "./components/layout/ClientContextGate";
import {
  AGENCY_PATH,
  AGENCY_TEAM_PATH,
  LIMITED_ACCESS_PATH,
  resolveLegacyWorkspacePath,
} from "./utils/agencyNavigation";
import "./App.css";
import "./components/agency/AgencyWorkspace.css";
import "./components/production/ProductionWorkspace.css";
import "./components/agency/AgencyModern.css";
import 'react-day-picker/style.css';
import './components/ui/ModernInputs.css';
import './components/agency/ClientPlan.css';

// Every product surface is split at the route boundary. The public landing
// page no longer downloads IA Studio, Konva, jsPDF, Recharts, Clair Labs and
// the admin dashboard before a visitor has even created an account.
const LandingPage = lazy(() => import("./components/pages/LandingPage"));
const LoginPage = lazy(() => import("./components/pages/LoginPage"));
const PortalLoginPage = lazy(() => import("./components/portal/PortalLoginPage"));
const AcceptInvitePage = lazy(
  () => import("./components/pages/AcceptInvitePage"),
);
const OnboardingPage = lazy(() => import("./components/pages/OnboardingPage"));
const AppLayout = lazy(() => import("./components/layout/AppLayout"));
const AgencyOverviewPage = lazy(
  () => import("./components/agency/AgencyOverviewPage"),
);
const AgencyClientPage = lazy(
  () => import("./components/agency/AgencyClientPage"),
);
const AgencySettingsPage = lazy(
  () => import("./components/agency/AgencySettingsPage"),
);
const ClientPortalPage = lazy(
  () => import("./components/portal/ClientPortalApp"),
);
const ProductionInboxPage = lazy(() => import("./components/production/ProductionInboxPage"));
const ProductionProjectPage = lazy(() => import("./components/production/ProductionProjectPage"));
const CampaignsPage = lazy(() => import("./components/pages/CampaignsPage"));
const NewCampaignPage = lazy(
  () => import("./components/pages/NewCampaignPage"),
);
const CampaignDetailPage = lazy(
  () => import("./components/pages/CampaignDetailPage"),
);
const IAStudioPage = lazy(() => import("./components/pages/IAStudioPage"));
const AssetLibraryPage = lazy(
  () => import("./components/pages/AssetLibraryPage"),
);
const BrandKitPage = lazy(() => import("./components/pages/BrandKitPage"));
const TemplateGalleryPage = lazy(
  () => import("./components/pages/TemplateGalleryPage"),
);
const SupportHubPage = lazy(() => import("./components/pages/SupportHubPage"));
const AdminPage = lazy(() => import("./components/pages/AdminPage"));
const TemplateBuilderPage = lazy(
  () => import("./components/pages/TemplateBuilderPage"),
);
const ClairLabsPage = lazy(
  () => import("./components/pages/ClairLabsRoutePage"),
);
const AICharactersPage = lazy(
  () => import("./components/pages/AICharactersPage"),
);
const NotFoundPage = lazy(() => import("./components/pages/NotFoundPage"));

function RouteLoader({ session = false }) {
  return (
    <div
      className="route-loader"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="route-loader-orbit" aria-hidden="true">
        <span />
        <span />
        <i />
      </div>
      <div className="route-loader-card">
        <LoadingRows
          label={
            session
              ? "Verificando tu sesión…"
              : "Preparando tu espacio creativo…"
          }
          accent="var(--module-accent, #1A56FF)"
          rows={3}
        />
      </div>
    </div>
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Keep diagnostic details in the console without exposing stack traces in
    // the customer-facing recovery screen.
    console.error("ClairAds Error:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <main className="fatal-state">
          <div className="fatal-state-orbit" aria-hidden="true">
            <RefreshCw size={26} />
          </div>
          <p className="fatal-state-kicker">Interrupción temporal</p>
          <h1>No pudimos mostrar esta pantalla</h1>
          <p>
            Tu trabajo guardado sigue seguro. Recarga la vista o vuelve al panel
            para continuar.
          </p>
          <div className="fatal-state-actions">
            <button type="button" onClick={() => window.location.reload()}>
              Recargar pantalla
            </button>
            <a href="/app">Volver al panel</a>
          </div>
        </main>
      );
    }
    return this.props.children;
  }
}

function ProtectedRoute({ children, portal = false }) {
  const { isAuthenticated, loading, isClientAccount } = useAuth();
  const location = useLocation();
  if (loading) return <RouteLoader session />;
  if (!isAuthenticated) return <Navigate to={portal ? "/portal/login" : "/login"} state={{ from: location.pathname + location.search }} replace />;
  if (isClientAccount && !portal) return <Navigate to="/portal" replace />;
  if (!isClientAccount && portal) return <Navigate to="/app/agency" replace />;
  return children;
}

function WorkspaceHome() {
  const { isAgencyStaff } = useAuth();
  return (
    <Navigate to={isAgencyStaff ? AGENCY_PATH : LIMITED_ACCESS_PATH} replace />
  );
}

function AgencyRoute({ children, adminOnly = false }) {
  const { isAgencyStaff, isAgencyAdmin } = useAuth();
  if (!isAgencyStaff) return <Navigate to={LIMITED_ACCESS_PATH} replace />;
  return adminOnly && !isAgencyAdmin ? (
    <Navigate to={AGENCY_PATH} replace />
  ) : (
    children
  );
}

function LegacyWorkspaceRoute() {
  const auth = useAuth();
  const location = useLocation();
  return (
    <Navigate
      to={resolveLegacyWorkspacePath(location.pathname, auth)}
      replace
    />
  );
}

function InternalAdminRoute() {
  const { isSuperadmin, isAgencyAdmin } = useAuth();
  return isSuperadmin ? (
    <AdminPage />
  ) : (
    <Navigate
      to={isAgencyAdmin ? AGENCY_TEAM_PATH : LIMITED_ACCESS_PATH}
      replace
    />
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Suspense fallback={<RouteLoader />}>
          <Routes>
            <Route path="/" element={isClairAdsHost() ? <LandingPage /> : <ClairLabsLandingPage />} />
            <Route path="/clairads" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/portal/login" element={<PortalLoginPage />} />
            <Route
              path="/register"
              element={<Navigate to="/login" replace />}
            />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route
              path="/portal/*"
              element={
                <ProtectedRoute portal>
                  <ClientPortalPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<WorkspaceHome />} />
              <Route
                path="agency"
                element={
                  <AgencyRoute>
                    <AgencyOverviewPage />
                  </AgencyRoute>
                }
              />
              <Route
                path="agency/clients/:clientId"
                element={
                  <AgencyRoute>
                    <AgencyClientPage />
                  </AgencyRoute>
                }
              />
              <Route path="agency/inbox" element={<AgencyRoute><ProductionInboxPage /></AgencyRoute>} />
              <Route path="ai-characters" element={<AgencyRoute><AICharactersPage /></AgencyRoute>} />
              <Route path="agency/plans" element={<LegacyWorkspaceRoute />} />
              <Route
                path="settings"
                element={
                  <AgencyRoute>
                    <AgencySettingsPage />
                  </AgencyRoute>
                }
              />
              <Route
                path="support"
                element={
                  <AgencyRoute>
                    <SupportHubPage />
                  </AgencyRoute>
                }
              />
              <Route path="select-client" element={<ClientSelectionPage />} />
              <Route path="access-limited" element={<LimitedAccessPage />} />
              <Route path="dashboard" element={<LegacyWorkspaceRoute />} />
              <Route path="credits" element={<LegacyWorkspaceRoute />} />
              <Route path="analytics" element={<LegacyWorkspaceRoute />} />
              <Route path="monitoring" element={<LegacyWorkspaceRoute />} />
              <Route element={<ClientContextGate />}>
                <Route path="campaigns" element={<CampaignsPage />} />
                <Route path="campaigns/new" element={<NewCampaignPage />} />
                <Route
                  path="campaigns/planner/:id"
                  element={<LegacyWorkspaceRoute />}
                />
                <Route
                  path="campaigns/calendar"
                  element={<LegacyWorkspaceRoute />}
                />
                <Route path="campaigns/:id" element={<CampaignDetailPage />} />
                <Route path="studio" element={<IAStudioPage />} />
                <Route
                  path="studio/templates/new"
                  element={<TemplateBuilderPage />}
                />
                <Route
                  path="studio/templates/:id/edit"
                  element={<TemplateBuilderPage />}
                />
                <Route path="clair-labs" element={<ClairLabsPage />} />
                <Route path="production/projects/:id" element={<ProductionProjectPage />} />
                <Route path="assets" element={<AssetLibraryPage />} />
                <Route path="brand-kit" element={<BrandKitPage />} />
                <Route path="templates" element={<TemplateGalleryPage />} />
              </Route>
              <Route path="admin" element={<InternalAdminRoute />} />
              <Route path="*" element={<NotFoundPage inApp />} />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </ErrorBoundary>
  );
}
